import React, { memo, useCallback } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";

export interface MenuItemType {
  id: string;
  label: string;
  icon: React.ReactNode;
  mobileLabel?: string;
  href?: string; 
  external?: boolean;
  disabled?: boolean;
}

interface SidebarMenuProps {
  title: string;
  menuItems: MenuItemType[];
  activeItem: string;
  onItemClick: (itemId: string) => void;
  actionButton?: {
    label: string;
    onClick: () => void;
    className?: string;
  };
  className?: string;
}

interface MenuButtonProps {
  active: boolean;
  onClick?: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
  href?: string;
  external?: boolean;
  disabled?: boolean;
  id: string;
}

const SidebarMenuButton = memo(({ 
  active, 
  onClick, 
  icon, 
  children, 
  href, 
  external, 
  disabled = false,
  id 
}: MenuButtonProps) => {
  const baseClassName = `
    group flex items-center justify-between w-full px-4 py-3 text-left text-sm md:text-base font-medium 
    transition-all duration-200 ease-in-out relative overflow-hidden
    ${disabled 
      ? "text-gray-400 cursor-not-allowed" 
      : active
        ? "text-teal-600 bg-teal-50 border-r-[3px] border-teal-600 shadow-sm"
        : "text-gray-700 hover:text-gray-900 hover:bg-gray-50 hover:shadow-sm"
    }
  `;

  const content = (
    <>
      {/* 활성 상태 배경 효과 */}
      {active && (
        <div className="absolute inset-0 bg-gradient-to-r from-teal-50 to-transparent opacity-50" />
      )}
      
      <div className="flex items-center gap-3 relative z-10">
        <div className={`transition-transform duration-200 ${active ? "scale-110" : "group-hover:scale-105"}`}>
          {icon}
        </div>
        <span className="truncate">{children}</span>
      </div>
      
      <ChevronRight className={`
        w-4 h-4 text-gray-400 transition-all duration-200 relative z-10
        ${active ? "text-teal-500 transform translate-x-1" : "group-hover:text-gray-600 group-hover:transform group-hover:translate-x-1"}
        hidden lg:block
      `} />
    </>
  );

  const commonProps = {
    className: baseClassName,
    'aria-label': `${children}으로 이동`,
    disabled,
    ...(active && { 'aria-current': 'page' as const })
  };

  if (href && !disabled) {
    if (external) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          {...commonProps}
          role="menuitem"
        >
          {content}
        </a>
      );
    } else {
      return (
        <Link 
          to={href} 
          target="_blank" 
          rel="noopener noreferrer" 
          {...commonProps}
          role="menuitem"
        >
          {content}
        </Link>
      );
    }
  }

  return (
    <button 
      onClick={disabled ? undefined : onClick} 
      {...commonProps}
      role="menuitem"
      id={`menu-${id}`}
    >
      {content}
    </button>
  );
});

const MobileTabButton = memo(({ 
  active, 
  onClick, 
  icon, 
  children, 
  href, 
  external, 
  disabled = false,
  id 
}: MenuButtonProps) => {
  const baseClassName = `
    group flex flex-col items-center gap-2 px-1 py-3 text-xs md:text-sm font-medium 
    transition-all duration-200 ease-in-out flex-1 min-w-0 relative overflow-hidden rounded-lg
    ${disabled 
      ? "text-gray-400 cursor-not-allowed" 
      : active
        ? "text-teal-600 bg-teal-50 shadow-sm scale-105"
        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
    }
  `;

  const content = (
    <>
      {/* 활성 상태 배경 효과 */}
      {active && (
        <div className="absolute inset-0 bg-gradient-to-b from-teal-100 to-teal-50 opacity-50 rounded-lg overflow-hidden" />
      )}
      
      <div className={`
        transition-all duration-200 relative z-10
        ${active ? "scale-110 text-teal-600" : "group-hover:scale-105"}
      `}>
        {icon}
      </div>
      
      <span className={`
        text-center leading-tight truncate w-full relative z-10
        ${active ? "font-semibold" : "group-hover:font-medium"}
      `}>
        {children}
      </span>
      
      {/* 활성 상태 인디케이터 */}
      {active && (
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-6 h-0.5 bg-teal-500 rounded-full" />
      )}
    </>
  );

  const commonProps = {
    className: baseClassName,
    'aria-label': `${children}으로 이동`,
    disabled,
    ...(active && { 'aria-current': 'page' as const })
  };

  if (href && !disabled) {
    if (external) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          {...commonProps}
          role="tab"
        >
          {content}
        </a>
      );
    } else {
      return (
        <Link 
          to={href} 
          {...commonProps}
          role="tab"
        >
          {content}
        </Link>
      );
    }
  }

  return (
    <button 
      onClick={disabled ? undefined : onClick} 
      {...commonProps}
      role="tab"
      id={`mobile-tab-${id}`}
      aria-selected={active}
    >
      {content}
    </button>
  );
});

const SidebarMenu = memo(({ 
  title, 
  menuItems, 
  activeItem, 
  onItemClick, 
  actionButton,
  className = ""
}: SidebarMenuProps) => {
  const handleItemClick = useCallback((itemId: string) => {
    onItemClick(itemId);
  }, [onItemClick]);

  return (
    <div className={className}>
      {/* 모바일 탭 메뉴 */}
      <div className="lg:hidden mb-6">
        
        <div className="bg-white border border-gray-200 rounded-xl p-2 shadow-sm">
          {/* 스크롤 가능한 탭 컨테이너 */}
          <div 
            className="flex gap-1 overflow-x-hidden scrollbar-none pb-1"
            role="tablist"
            aria-label={`${title} 네비게이션`}
          >
            {menuItems.map((item) => (
              <MobileTabButton
                key={item.id}
                id={item.id}
                active={activeItem === item.id}
                onClick={item.href ? undefined : () => handleItemClick(item.id)}
                href={item.href}
                external={item.external}
                disabled={item.disabled}
                icon={item.icon}
              >
                {item.mobileLabel ? (
                  <span dangerouslySetInnerHTML={{ __html: item.mobileLabel }} />
                ) : (
                  item.label
                )}
              </MobileTabButton>
            ))}
          </div>
        </div>
      </div>

      {/* 데스크톱 사이드바 */}
      <aside className="w-64 flex-shrink-0 hidden lg:block">
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
          {/* 헤더 */}
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <h2 className="text-lg md:text-xl font-bold text-gray-900 flex items-center gap-2">
              {title}
            </h2>
          </div>
          
          {/* 네비게이션 메뉴 */}
          <nav role="menu" aria-label={`${title} 메뉴`}>
            {menuItems.map((item) => (
              <SidebarMenuButton
                key={item.id}
                id={item.id}
                active={activeItem === item.id}
                onClick={item.href ? undefined : () => handleItemClick(item.id)}
                href={item.href}
                external={item.external}
                disabled={item.disabled}
                icon={item.icon}
              >
                {item.label}
              </SidebarMenuButton>
            ))}
          </nav>

          {/* 액션 버튼 */}
          {actionButton && (
            <div className="p-4 border-t border-gray-200 bg-gray-50">
              <p className="text-sm font-medium text-gray-900 mb-3">
                도움이 필요하신가요?
              </p>
              <button
                onClick={actionButton.onClick}
                className={`
                  w-full px-4 py-2 text-sm font-medium rounded-lg
                  transition-all duration-200 ease-in-out
                  hover:shadow-md active:scale-95
                  ${actionButton.className || "bg-emerald-500 text-white hover:bg-emerald-600"}
                `}
                aria-label={actionButton.label}
              >
                {actionButton.label}
              </button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
});

// 컴포넌트 이름 설정 (디버깅용)
SidebarMenuButton.displayName = 'SidebarMenuButton';
MobileTabButton.displayName = 'MobileTabButton';
SidebarMenu.displayName = 'SidebarMenu';

export default SidebarMenu; 